"use client";

import { useEffect, useState } from "react";
import { MailWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth/use-session";

/**
 * Shown to signed-in users who have not confirmed their email.
 * Confirming unlocks the rest of the free credits. Soft: nothing is blocked.
 */
export function VerifyEmailBanner() {
	const { user } = useSession();
	const [unverified, setUnverified] = useState(false);
	const [busy, setBusy] = useState(false);
	const [note, setNote] = useState<string | null>(null);

	useEffect(() => {
		if (!user) return;
		let cancelled = false;
		fetch("/api/auth/me", { credentials: "include", cache: "no-store" })
			.then((r) => (r.ok ? r.json() : null))
			.then((d) => { if (!cancelled && d && d.email_verified === false) setUnverified(true); })
			.catch(() => {});
		return () => { cancelled = true; };
	}, [user]);

	if (!user || !unverified) return null;

	async function resend() {
		setBusy(true);
		setNote(null);
		try {
			const r = await fetch("/api/auth/resend-verification", { method: "POST", credentials: "include" });
			const d = await r.json().catch(() => ({}));
			if (r.ok && d.already_verified) { setUnverified(false); return; }
			setNote(r.ok ? "Sent. Check your inbox and spam folder." : d.detail ?? "Could not send the email. Try again in a minute.");
		} catch {
			setNote("Could not send the email. Check your connection.");
		} finally {
			setBusy(false);
		}
	}

	return (
		<div data-testid="verify-banner" role="status" className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-secondary px-4 py-2.5 text-sm text-secondary-foreground">
			<MailWarning className="size-4 shrink-0" aria-hidden />
			<span className="min-w-0 flex-1">Confirm <b className="font-semibold">{user.email}</b> to unlock the rest of your free credits.</span>
			{note && <span className="text-xs" data-testid="verify-note">{note}</span>}
			<Button type="button" variant="outline" size="sm" className="min-h-9" onClick={resend} disabled={busy} data-testid="verify-resend">
				{busy ? "Sending…" : "Resend email"}
			</Button>
		</div>
	);
}
